import { useEffect, useMemo, useState } from "react";
import { api } from "../../api";
import { navigate, usePathname } from "../../lib/router";
import { Button } from "../../components/ui/button";
import { Sidebar, menuItems, type MenuItem } from "../../components/Sidebar";
import { sidebarLabels } from "../../components/Sidebar.labels";
import { Menu, X, FileText } from "../../components/icons";
import { CompanyProvider } from "../contexts/CompanyContext";
import { useLanguage } from "../..//contexts/LanguageContext";
import { appShellLabels } from "./labels";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export function AppShell({ children }: { children: React.ReactNode }) {
  const { lang } = useLanguage();
  const t = appShellLabels[lang];
  const s = sidebarLabels[lang];
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<{
    name: string;
    email: string;
    company_id: number | null;
    is_admin: boolean;
  } | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarTitle, setSidebarTitle] = useState<string | null>(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const path = usePathname();
  const [fromApp, setFromApp] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  const handleLogout = async () => {
    await api.logout();
    localStorage.clear();
    navigate("/login");
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const src = params.get("source");
    if (src) {
      localStorage.setItem("source", src);
    }
    setFromApp(localStorage.getItem("source") === "app2");
  }, []);

  const items: MenuItem[] = useMemo(() => {
    const base = menuItems.filter(item =>
      fromApp ? item.href !== "/app/assinatura" : true,
    );
    if (user?.email === "lelo@gmail.com") {
      base.push({
        href: "/app/blog",
        label: "Blog",
        Icon: FileText,
        match: p => p.startsWith("/app/blog"),
      });
    }
    return base;
  }, [user, fromApp]);

  useEffect(() => {
    (async () => {
      const me = await api.me();
      if (!me.ok) {
        navigate("/login");
        return;
      }
      setUser(me.data);
      // Load company data for sidebar title and subscription status
      const comp = await api.getCompany();
      if (comp.ok) {
        setSidebarTitle(comp.data.name ?? null);
        const plan = comp.data.plan;
        const next = comp.data.next_renewal;
        setHasSubscription(!!plan && (!next || new Date(next) > new Date()));
        // Floating time model: ignore company timezone
      }
      setLoading(false);
    })();
  }, []);

  // Initialize sidebar from persisted preference or breakpoint
  useEffect(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("sidebarOpen");
      if (stored === "1" || stored === "0") {
        setSidebarOpen(stored === "1");
      } else {
        setSidebarOpen(window.innerWidth >= 768);
      }
    }
  }, []);

  // Persist preference when user toggles
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("sidebarOpen", sidebarOpen ? "1" : "0");
    }
  }, [sidebarOpen]);

  // Prevent background scroll when mobile drawer is open
  useEffect(() => {
    if (typeof window !== "undefined") {
      const isMobile = window.innerWidth < 768;
      if (isMobile && sidebarOpen) document.body.classList.add("overflow-hidden");
      else document.body.classList.remove("overflow-hidden");
      return () => document.body.classList.remove("overflow-hidden");
    }
  }, [sidebarOpen]);

  if (loading) return <div className="p-6 text-neutral-700">Carregando...</div>;

  const go = (href: string) => {
    navigate(href);
    // Close mobile menu after navigation
    if (typeof window !== "undefined" && window.innerWidth < 768) setSidebarOpen(false);
  };
  const link = (href: string, label: string) => (
    <a
      href={href}
      onClick={e => {
        e.preventDefault();
        go(href);
      }}
      className={`px-3 py-2 rounded-md text-sm ${path === href ? "bg-neutral-900 text-white" : "text-neutral-800 hover:bg-neutral-100"}`}
    >
      {label}
    </a>
  );

  return (
    <CompanyProvider value={{ sidebarTitle, setSidebarTitle }}>
    <div className="min-h-screen w-full bg-neutral-50 text-neutral-900 flex items-start">
      {/* Desktop sidebar as component (collapsible) */}
      <Sidebar
        expanded={sidebarOpen}
        path={path}
        onNavigate={go}
        onToggle={() => setSidebarOpen(o => !o)}
        userName={user?.name ?? null}
        onLogout={() => setConfirmLogout(true)}
        title={sidebarTitle || "Agenda"}
        items={items}
      />
      {/* Mobile sidebar (drawer) */}
      <div
        className={`md:hidden fixed inset-0 z-40 ${sidebarOpen ? "" : "pointer-events-none"}`}
        aria-hidden={!sidebarOpen}
      >
        {/* backdrop */}
        <div
          className={`absolute inset-0 bg-black/30 transition-opacity duration-200 ease-out ${sidebarOpen ? "opacity-100" : "opacity-0"}`}
          onClick={() => setSidebarOpen(false)}
        />
        {/* drawer */}
        <aside
          className={`absolute inset-y-0 left-0 w-64 bg-white border-r border-neutral-200 transform transition-transform duration-300 ease-in-out ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
        >
          <div className="h-14 px-4 flex items-center justify-between font-semibold">
            <span>{sidebarTitle || "Agenda"}</span>
            <Button
              variant="ghost"
              className="h-8 w-8 p-0"
              aria-label="Fechar"
              onClick={() => setSidebarOpen(false)}
              icon={<X className="h-5 w-5" />}
            />
          </div>
          <nav className="px-2 py-2 space-y-1">
            {items.map(({ href, label, Icon, match }) => (
              <a
                key={href}
                href={href}
                onClick={e => { e.preventDefault(); go(href); }}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm ${match(path) ? "bg-neutral-900 text-white" : "hover:bg-neutral-100"}`}
              >
                <Icon className="w-5 h-5" />
                <span>{label}</span>
              </a>
            ))}
            <div className="mt-4 px-3 text-xs text-neutral-500">
              <div>{user?.name}</div>
              <button
                className="mt-2 text-left underline underline-offset-2"
                onClick={() => setConfirmLogout(true)}
              >{s.logout}</button>
            </div>
          </nav>
        </aside>
      </div>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="fixed md:static top-0 z-30 h-14 w-full border-b border-neutral-200 bg-white px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              className="h-8 w-8 p-0 md:hidden"
              aria-label="Abrir menu"
              onClick={() => setSidebarOpen(true)}
              icon={<Menu className="h-5 w-5" />}
            />
            <div className="font-medium">Dashboard</div>
          </div>

        </header>
        <div className="h-14 md:hidden" />
        <main className="p-4 md:p-6 flex-1 min-h-0">{children}</main>
      </div>
    </div>
    <AlertDialog open={confirmLogout} onOpenChange={setConfirmLogout}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t.logoutDialog.title}</AlertDialogTitle>
          <AlertDialogDescription>
            {t.logoutDialog.description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t.logoutDialog.cancel}</AlertDialogCancel>
          <AlertDialogAction onClick={handleLogout}>
            {t.logoutDialog.confirm}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </CompanyProvider>
  );
}
